import { useState } from "react";

import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import { InboundLayout, InboundHeaderCard } from "ui-components/Layout";
import { InboundSaveButton, InboundDetailEditButton } from "ui-components/Form";

import { headerRender, headerValues, headerValidation } from "./co-receiving-schema";
import { detailRender, detailValues, detailValidation } from "./co-receiving-details-schema";
import { Success, Error_, InfoDialog, LoadingDialog } from "ui-components/Alert";
import { skuValidation } from "layouts/views/common/validation";
import { dateValidation } from "layouts/views/common/validation";
import { useMutation, useQuery } from "@tanstack/react-query";
import COReceiving from "./components/COReceiving";
import COReceivingDetail from "./components/COReceivingDetail";
import { coRecvColumns } from "./columns";
import { getCOReceiving, editCOReceiving } from "services/data.inbound.service";
import { useLocation, useNavigate } from "react-router-dom";
import BasicTable2 from "ui-components/tables/BasicTable2";
import { statusLabel, statusObj } from "layouts/views/common/enum";
import ApplyAll from "../components/ApplyAll";
import {
  cancelDetail,
  deleteDetail,
  getSplitDetailsTotalQuantity,
  getTotalQuantity,
  updateDetail,
} from "../common/common";
import { CORecvCaption } from "../components/Caption";

function EditCOReceiving() {
  const location = useLocation();
  const navigate = useNavigate();
  const { recvCode, customerCode } = location.state;
  const [formValues, setFormValues] = useState(headerValues);
  const [alert, setAlert] = useState(null);
  const [details, setDetails] = useState([]);
  const onError = (data) => setAlert(<Error_ data={data} onChange={setAlert} />);
  const editCOReceivingMutation = useMutation(editCOReceiving, {});

  const { isFetching } = useQuery({
    queryKey: ["co-recv", { recvCode }],
    queryFn: () => getCOReceiving({ recvCode }),
    onSuccess: (data) => {
      const { status, coRecvDetails, recvType, ...coRecvValues } = data.data.data;
      setFormValues({
        ...coRecvValues,
        status: statusLabel[status],
        recvType: "Consignee Return Receipt (Trade Return)",
      });
      setDetails(
        coRecvDetails.map((detail) => ({ ...detail, status: statusLabel[detail.status] }))
      );
    },
  });

  const form = useForm({
    defaultValues: formValues,
    values: formValues,
    resolver: yupResolver(headerValidation),
  });
  const {
    control,
    watch,
    formState: { errors, isSubmitting },
    setValue,
    getValues,
    trigger,
  } = form;
  const headerProps = {
    headerRender,
    customerCode,
    control,
    setValue,
    errors,
    mode: "EDIT",
  };

  //const [vendorCode] = watch(["vendorCode"]);

  const detailProps = { details, setDetails, values: { customerCode } };

  const onSubmit = async () => {
    window.scrollTo(0, 0);
    const isValid = await trigger();
    if (!isValid) return;

    const data = getValues();

    if (details.length === 0) {
      setAlert(<Error_ content="Invalid CO Receiving Details" onChange={setAlert} />);
      return;
    }

    const req = {
      ...data,
      coRecvDetails: details.map((d) => ({ ...d, status: statusObj[d.status] })),
    };

    editCOReceivingMutation.mutate(
      { recvCode, customerCode, req },
      {
        onSuccess: (data) => navigate("/inbound/co-receiving", { state: data.data.message }),
        onError,
      }
    );
  };
  return (
    <InboundLayout lg={12}>
      {isFetching && <LoadingDialog />}
      {alert}
      <InboundHeaderCard>
        <form noValidate>
          <COReceiving {...headerProps} />
        </form>
      </InboundHeaderCard>

      <Detail {...detailProps} />
      <InboundSaveButton url="/inbound/co-receiving" onClick={onSubmit} disabled={isSubmitting} />
    </InboundLayout>
  );
}

const Detail = ({ details, setDetails, values }) => {
  const columns = [
    {
      field: "actions",
      headerName: "Action",
      renderActions: ["Add", "Cancel"],
      type: "actions",
    },
    ...coRecvColumns,
  ];
  const [alert, setAlert] = useState(null);

  const [formValues, setFormValues] = useState(null);
  const [dialog, setDialog] = useState(null);
  const lineNo = formValues ? formValues.lineNo : details.length + 1;

  const form = useForm({
    defaultValues: { ...detailValues, lineNo },
    values: formValues || { ...detailValues, lineNo, status: "" },
    resolver: yupResolver(detailValidation),
  });
  const {
    control,
    watch,
    formState: { errors, isDirty },
    setValue,
    getValues,
    handleSubmit,
  } = form;
  const [skuCode, qtyReceived, recvWh, recvZone] = watch([
    "skuCode",
    "qtyReceived",
    "recvWh",
    "recvZone",
  ]);

  const onSubmit = (data) => {
    setDetails(updateDetail(details, data));
    setFormValues(null);
    setAlert(<Success content="Updated" onChange={setAlert} />);
  };
  const onRowClick = (values) => {
    if (formValues !== null && isDirty) {
      setDialog(
        <InfoDialog
          title={`Unsaved changes for Line No ${formValues.lineNo}`}
          content="Do you want to save the changes"
          onOk={() => {
            const data = getValues();
            setDetails(updateDetail(details, data));
            setDialog(null);
            setFormValues(values);
          }}
          onClose={() => {
            setDialog(null);
            setFormValues(values);
          }}
        />
      );
    } else {
      setFormValues(values);
    }
    return true;
  };
  const handleCancel = (data) => {
    setDetails(cancelDetail(details, data));
    setFormValues(null);
    setAlert(<Success content="Cancelled" onChange={setAlert} />);
  };
  const handleDelete = (data) => {
    if (data.newItem !== true) return;
    setDetails(deleteDetail(details, data));
    setFormValues(null);
    setAlert(<Success content="Deleted" onChange={setAlert} />);
  };
  const onAdd = (data) => {
    const currentLine = details.findIndex(x => x.lineNo == data.lineNo) + 1;
    const newLine = { ...data, lineNo: details.length + 1, newItem: true };
    const updatedDetail = [
      ...details.slice(0, currentLine),
      newLine,
      ...details.slice(currentLine, details.length + 1)
    ]
    setDetails(updatedDetail);
    setAlert(<Success content="Added" onChange={setAlert} />);
  };

  const detailProps = {
    detailRender,
    values: { ...values, skuCode, qtyReceived, recvWh, recvZone },
    control,
    errors,
    setValue,
    getValues,
    mode: "EDIT",
  };
  const totalQuantity = getTotalQuantity(details, "quantity");
  const totalExpectedQuantity = getSplitDetailsTotalQuantity(details, "qtyExpected", "coLineNo");
  const Caption = (
    <CORecvCaption
      totalRows={details.length}
      totalQuantity={totalQuantity}
      totalExpectedQuantity={totalExpectedQuantity}
    />
  );
  const applyAllProps = { formValues, setFormValues,setValue, details, setDetails };
  return (
    <InboundHeaderCard sx={{ height: "100%" }}>
      {alert}
      <ApplyAll {...applyAllProps} />

      {dialog}
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <COReceivingDetail {...detailProps} />
        {formValues !== null && (
          <InboundDetailEditButton disabled={!isDirty} onClick={() => setFormValues(null)} />
        )}
      </form>

      <BasicTable2
        columns={columns}
        rows={details}
        caption={Caption}
        onCancel={handleCancel}
        onRowClick={onRowClick}
        onDelete={handleDelete}
        onAdd={onAdd}
        mode="EDIT"
      />
    </InboundHeaderCard>
  );
};

export default EditCOReceiving;
